<template lang="pug">
.d-flex.align-items-start.flex-wrap.gap-20
  template(v-if="!isUser")
    .d-flex.flex-auto.gap-10
      .min-w-15rem.max-w-20rem.flex-1

        AppLazySelect(
          resource="member"
          :fetcher="fetchMembers"
          :value="userId ? selectedMember : null"
          title="All members"
          :disabled="disabled"
          data-testid="insights-member-select"
          eager
          @input="setMember"
        )
          template(#placeholder)
            | All members

          template(#item-leading="{ item }")
            AppAvatar(
              :avatar="item.avatar"
              size="speck"
            )
      .min-w-15rem.max-w-20rem.flex-1
        AppLazySelect(
          resource="team"
          :fetcher="fetchTeams"
          :value="selectedTeam"
          title="All teams"
          :disabled="disabled"
          eager
          @input="setTeam"
        )
          template(#placeholder)
            | All teams

          template(#item-leading="{ item }")
            AppAvatarInitial(
              :text="item.name"
              size="speck"
            )

  .d-flex.align-items-start.gap-10.flex-wrap
    .d-flex.gap-5
      AppButton.min-w-unset(
        :disabled="disabled || arrowBackDisabled"
        @click="moveBack"
      )
        span.hi-thin-left-arrow
      AppButton.min-w-unset(
        :disabled="disabled"
        @click="moveForward"
      )
        span.hi-thin-right-arrow
      AppButton.min-w-unset(
        v-if="!userId"
        :disabled="disabled"
        @click="gotoToday"
      )
        span Today
      AppButton.min-w-unset(
        v-if="userId"
        :disabled="disabled"
        @click="gotoThisWeek"
      )
        span This week
    .d-flex.gap-5
      AppDateRangePicker.w-full.flex-1(
        v-if="userId"
        opens="right"
        :start-date="fromMoment.clone()"
        :end-date="toMoment.clone()"
        :min-date="minDate"
        :start-week-on="startWeekOn"
        :current-zone="pickerZone"
        :custom-ranges="dateRanges"
        :disabled="disabled"
        @change="setDateRange"
      )
      AppDatePicker.w-full(
        v-else
        type="daily"
        extra-picker-classes="max-w-unset"
        opens="center"
        :time-zone="timeZone"
        :value="fromMoment.clone()"
        :min-date="minDate"
        :start-week-on="startWeekOn"
        :show-quick-pick="false"
        :current-zone="pickerZone"
        :disabled="disabled"
        @input="setDate"
      )
      InsightsHeaderTimeZoneFilter(
        direction="end"
        :disabled="disabled"
        trigger-classes="btn-default h-40"
        show-description
        @input="changeTimeZone"
      )
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters, mapState } from 'vuex';

import AppDatePicker from '~/components/AppDatePicker.vue';
import AppLazySelect from '~/components/AppLazySelect.vue';
import AppAvatar from '~/components/AppAvatar.vue';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';
import AppButton from '~/components/AppButton.vue';
import InsightsHeaderTimeZoneFilter from '~/components/insights/InsightsHeaderTimeZoneFilter.vue';
import { InsightsDateRanges } from '~/mixins';
import { InsightsResource } from '~/resources';
import { clampToInsightsLowerLimit, getInsightsLowerLimit } from '~/utils/insights_utils';

export default {
  name: 'InsightsHeaderTimeline',
  components: {
    AppLazySelect,
    InsightsHeaderTimeZoneFilter,
    AppDatePicker,
    AppAvatar,
    AppAvatarInitial,
    AppButton,
  },
  mixins: [InsightsDateRanges],
  props: {
    value: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['team', 'member', 'timezone', 'date'],
  data: () => {
    return {
      team: null,
    };
  },
  computed: {
    ...mapState({
      activityRetentionStart: (state) => state.activity_retention_start,
    }),
    ...mapGetters([
      'canSeeTeam',
      'organizationId',
      'startWeekOn',
      'teamId',
      'timeZone',
      'userId',
    ]),
    isUser() {
      return !this.canSeeTeam;
    },
    selectedMember() {
      // Use the passed user data if available
      return this.user;
    },
    selectedTeam() {
      return this.teamId ? this.team : null;
    },
    isoWeekday() {
      return toMomentIsoWeekday(this.startWeekOn);
    },
    fromMoment() {
      return this.momentInZone(this.value.from);
    },
    toMoment() {
      // The multi-user timeline shows a single day driven by the range start.
      // Ignore a multi-day `to` carried over from another tab so the arrows
      // step one day at a time instead of the inherited range length.
      return this.userId ? this.momentInZone(this.value.to) : this.fromMoment.clone();
    },
    minDateMoment() {
      return getInsightsLowerLimit({
        timeZone: this.timeZone,
        useRetentionLimit: true,
        activityRetentionStart: this.activityRetentionStart,
      });
    },
    minDate() {
      return this.minDateMoment.format('YYYY-MM-DD');
    },
    pickerZone() {
      return { for: this.timeZone };
    },

    arrowBackDisabled() {
      const lowerLimit = this.minDateMoment;
      const fromDate = this.fromMoment.clone().startOf('day');

      return fromDate.isSameOrBefore(lowerLimit, 'day');
    },
  },
  watch: {
    teamId: {
      immediate: true,
      async handler(newTeamId) {
        if (newTeamId && !this.team) {
          try {
            const response = await this.fetchTeams({
              search: '',
              filters: { teams: true },
            });
            // Find the team with matching ID
            const team = response.items?.find((t) => t.id === newTeamId);

            if (team) {
              this.team = team;
            }
          } catch (error) {
            console.error('Failed to fetch team data:', error);
          }
        }
      },
    },
  },
  methods: {
    ...mapActions(['filterChange']),
    fetchMembers(payload) {
      return InsightsResource.members({ organizationId: this.organizationId }, payload, {
        abortReload: true,
      });
    },
    fetchTeams(payload) {
      return this.fetchMembers({
        ...payload,
        filters: { ...payload.filters, teams: true },
      });
    },
    setTeam(team) {
      this.team = team;

      this.$emit('team', team);
    },
    setMember(member) {
      this.$emit('member', member);
    },
    changeTimeZone(tzParam, mode) {
      this.$emit('timezone', tzParam, mode);
    },
    setDate(date) {
      const clamped = this.clampToMinDate(date);
      const from = clamped.format('YYYY-MM-DD');
      const to = from;

      this.emitDateChange(from, to);
    },

    setDateRange({ start, end }) {
      const from = this.clampToMinDate(start);
      const to = this.clampToMinDate(end);
      const adjustedTo = to.isBefore(from, 'day') ? from.clone() : to;

      this.emitDateChange(from.format('YYYY-MM-DD'), adjustedTo.format('YYYY-MM-DD'));
    },
    emitDateChange(from, to) {
      this.$emit('date', { query: { key: 'date', initial: false, value: { from, to } } });
    },
    isMonthAligned(from, to) {
      if (from.date() !== 1) { return false; }
      if (!to.isSame(from, 'month')) { return false; }

      return to.clone().add(1, 'day').date() === 1;
    },
    moveBack() {
      const fromDate = this.fromMoment;
      const toDate = this.toMoment;
      const daysDiff = toDate.diff(fromDate, 'days');

      if (daysDiff === 0) {
        this.setDate(fromDate.clone().subtract(1, 'day'));
      } else if (this.isMonthAligned(fromDate, toDate)) {
        const prevMonthStart = fromDate.clone().subtract(1, 'month').startOf('month');
        const prevMonthEnd = fromDate.clone().subtract(1, 'day');

        this.setDateRange({ start: prevMonthStart, end: prevMonthEnd });
      } else {
        this.setDateRange({
          start: fromDate.clone().subtract(daysDiff + 1, 'days'),
          end: toDate.clone().subtract(daysDiff + 1, 'days'),
        });
      }
    },
    moveForward() {
      const fromDate = this.fromMoment;
      const toDate = this.toMoment;
      const daysDiff = toDate.diff(fromDate, 'days');

      if (daysDiff === 0) {
        this.setDate(fromDate.clone().add(1, 'day'));
      } else if (this.isMonthAligned(fromDate, toDate)) {
        const nextMonthStart = toDate.clone().add(1, 'day').startOf('month');
        const nextMonthEnd = nextMonthStart.clone().endOf('month');

        this.setDateRange({ start: nextMonthStart, end: nextMonthEnd });
      } else {
        this.setDateRange({
          start: fromDate.clone().add(daysDiff + 1, 'days'),
          end: toDate.clone().add(daysDiff + 1, 'days'),
        });
      }
    },
    gotoToday() {
      this.setDate(moment().tz(this.timeZone));
    },
    gotoThisWeek() {
      const now = moment().tz(this.timeZone);
      const isoWeekday = toMomentIsoWeekday(this.startWeekOn);
      const startOfWeek = now.clone().startOfIsoWeek(isoWeekday);
      const endOfWeek = now.clone().endOfIsoWeek(isoWeekday);

      this.setDateRange({ start: startOfWeek, end: endOfWeek });
    },
    momentInZone(date) {
      return moment.tz(date, this.timeZone);
    },
    clampToMinDate(date) {
      const lowerLimit = this.minDateMoment;

      return clampToInsightsLowerLimit(date, lowerLimit);
    },
  },
};
</script>
